import type { MarketingUTMs } from 'common/features/store/duck/utms/duck';

export type { MarketingUTMs };

export const extractUTMsFromURL = (url?: string): MarketingUTMs => {
    try {
        let searchParams: URLSearchParams;

        if (url) {
            try {
                searchParams = new URL(url, window.location.origin).searchParams;
            } catch (error) {
                console.warn(
                    '[extractUTMsFromURL] Failed to parse provided URL, using current location:',
                    error
                );
                searchParams = new URLSearchParams(window.location.search || '');
            }
        } else {
            const searchString = window.location?.search || '';
            searchParams = new URLSearchParams(searchString);
        }

        const extractedUTMs: MarketingUTMs = {};

        const utmParameterNames = [
            'utm_source',
            'utm_medium',
            'utm_campaign',
            'utm_content',
            'utm_id',
            'utm_channel',
        ] as const;

        utmParameterNames.forEach((parameterName) => {
            try {
                const parameterValue = searchParams.get(parameterName);
                if (parameterValue) {
                    extractedUTMs[parameterName] = parameterValue;
                }
            } catch (error) {
                console.warn(
                    `[extractUTMsFromURL] Error reading UTM parameter ${parameterName}:`,
                    error
                );
            }
        });

        return extractedUTMs;
    } catch (error) {
        return {};
    }
};

export const mergeUTMsForAffiliateLink = (
    marketingUTMs: MarketingUTMs,
    productUTMs: MarketingUTMs
): MarketingUTMs => {
    try {
        const mergedUTMs: MarketingUTMs = {};

        const safeMarketingUTMs = marketingUTMs || {};
        const safeProductUTMs = productUTMs || {};

        Object.assign(mergedUTMs, safeProductUTMs);

        Object.keys(safeMarketingUTMs).forEach((key) => {
            const utmKey = key as keyof MarketingUTMs;
            const marketingValue = safeMarketingUTMs[utmKey];

            if (utmKey === 'utm_content' && mergedUTMs.utm_content) {
                return;
            }

            if (marketingValue && typeof marketingValue === 'string') {
                mergedUTMs[utmKey] = marketingValue;
            }
        });

        return mergedUTMs;
    } catch (error) {
        return productUTMs || {};
    }
};

export const applyUTMsToURL = (url: string, utms: MarketingUTMs): string => {
    if (!url || typeof url !== 'string') {
        return url || '';
    }

    try {
        const urlObject = new URL(url);
        Object.entries(utms || {}).forEach(([key, value]) => {
            if (value && typeof value === 'string') {
                urlObject.searchParams.set(key, value);
            }
        });
        return urlObject.toString();
    } catch (error) {
        try {
            const separator = url.includes('?') ? '&' : '?';
            const utmParameters = new URLSearchParams();
            Object.entries(utms || {}).forEach(([key, value]) => {
                if (value && typeof value === 'string') {
                    utmParameters.set(key, value);
                }
            });
            const parameterString = utmParameters.toString();
            return parameterString ? `${url}${separator}${parameterString}` : url;
        } catch (fallbackError) {
            return url;
        }
    }
};
